import React, { useState, useEffect } from 'react';
import { SignUpButton } from "@clerk/clerk-react";
import { fetchProPrice, pricingPlans } from '../data/pricing';

const buttonText = (plan) => 
    plan.title === 'Free' ? 'Start Finding Opportunities' : 
    plan.title === 'Pro' ? 'Claim My 7-Day Advantage' : 
    'Get Started';

const getPriceText = (plan, proPrice) => {
    if (plan.title === 'Free') return 'Free';
    if (plan.title !== 'Pro') return `${plan.price}`;
    if (proPrice.status === 'success') return proPrice.value;
    if (proPrice.status === 'error') return 'Unavailable';

    return 'Loading...';
};

const useProPrice = () => {
    const [proPrice, setProPrice] = useState({
        status: 'loading',
        value: null,
        error: null,
    });

    useEffect(() => {
        let isMounted = true;

        const loadProPrice = async () => {
            try {
                const value = await fetchProPrice();
                if (isMounted) {
                    setProPrice({ status: 'success', value, error: null });
                }
            } catch (error) {
                if (isMounted) {
                    setProPrice({ status: 'error', value: null, error: error.message });
                }
            }
        };

        loadProPrice();

        return () => {
            isMounted = false;
        };
    }, []);

    return proPrice;
};

const PricingSection = () => {
    const proPrice = useProPrice();

    return (
        <section id="pricing" className="bg-[#121212] py-20 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-gray-400 text-sm uppercase mb-4">Choose Your Advantage</h2>
                    <h1 className="text-white text-5xl font-bold mb-6">
                        Start Free. Scale Fast.
                        <br />
                        <span className="text-[#7C3AED]">Stay Ahead Forever.</span>
                    </h1>
                    <p className="text-gray-400 text-xl">
                        Join other product builders who found their breakthrough before the competition. Try Pro free for 7 days.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {pricingPlans.map((plan) => (
                        <div 
                            key={plan.priceId || `plan-${plan.title}`}
                            className="bg-[#1c1c1c] rounded-2xl p-8 border border-gray-800 relative"
                        >
                            {plan.title === 'Pro' && (
                                <div className="absolute top-4 right-4 bg-[#7C3AED] text-white px-3 py-1 rounded-full text-sm font-semibold">
                                    Pro
                                </div>
                            )}
                            <div className="mb-8">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-white text-6xl font-bold">
                                        {getPriceText(plan, proPrice)}
                                    </span>
                                    {plan.title !== 'Free' && (
                                        <span className="text-gray-400">per month</span>
                                    )}
                                </div>
                                {plan.title === 'Pro' ? (
                                    <div className="mt-2">
                                        {proPrice.status === 'error' && (
                                            <span className="text-gray-400 text-sm block mb-1">
                                                {proPrice.error}
                                            </span>
                                        )}
                                        <span className="text-[#7C3AED] text-sm font-medium">7-day free trial</span>
                                    </div>
                                ) : (
                                    <div className="mt-2">
                                        <span className="text-sm font-medium invisible">Placeholder text</span>
                                    </div>
                                )}
                            </div>
                            
                            {plan.description && (
                                <p className="text-gray-400 mb-8">{plan.description}</p>
                            )}
                            
                            <SignUpButton mode="modal">
                                <button className="w-full bg-[#7C3AED] text-white px-6 py-4 rounded-lg mb-8 hover:bg-[#6D28D9] transition-colors text-lg font-medium">
                                    {buttonText(plan)}
                                </button>
                            </SignUpButton>

                            <div className="space-y-4">
                                <h4 className="text-gray-400 font-medium mb-4">FEATURES</h4>
                                {plan.features.map((feature, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <span className="text-[#7C3AED] mt-1">✓</span>
                                        <span className="text-gray-400">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default PricingSection;
